import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const HIGH_SPEED_ACK = 0x16
const MEMORY_SIZE = 0xd100
const ZONE_START = 0x0a00
const SKIP_START = 0xc570
const CHANNEL_COUNT = 512
const CHANNEL_SIZE = 48

type TKx180Spec = {
  id: string
  model: string
  identity: readonly number[]
  summary: string
}

export type TKx180Profile = {
  protocol: 'kenwood-tkx180'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  stopBits: 2
  channelMinimum: 1
  channelMaximum: 512
  nameLength: 12
  memorySize: number
  identity: readonly number[]
  powerLevels: readonly ['Low', 'High']
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

const SPECS: readonly TKx180Spec[] = [
  { id: 'kenwood-tk-7180', model: 'TK-7180', identity: ascii("M7180\u0004"), summary: 'Kenwood TK-7180 VHF mobile.' },
  { id: 'kenwood-tk-8180', model: 'TK-8180', identity: ascii("M8180\u0006"), summary: 'Kenwood TK-8180 UHF mobile.' },
  { id: 'kenwood-tk-2180', model: 'TK-2180', identity: ascii("P2180\u0004"), summary: 'Kenwood TK-2180 VHF portable.' },
  { id: 'kenwood-tk-3180k', model: 'TK-3180K', identity: ascii("P3180\u0006"), summary: 'Kenwood TK-3180K UHF portable.' },
  { id: 'kenwood-tk-3180k2', model: 'TK-3180K2', identity: ascii("P3180\u0007"), summary: 'Kenwood TK-3180K2 UHF portable.' },
  { id: 'kenwood-tk-8180e', model: 'TK-8180E', identity: ascii("M8189'"), summary: 'Kenwood TK-8180E UHF mobile.' },
  { id: 'kenwood-tk-7180e', model: 'TK-7180E', identity: ascii('M7189$'), summary: 'Kenwood TK-7180E VHF mobile.' },
  { id: 'kenwood-tk-3180e', model: 'TK-3180E', identity: ascii("P3189'"), summary: 'Kenwood TK-3180E UHF portable.' },
]

export const TKX180_PROFILES: readonly TKx180Profile[] = SPECS.map((spec) => ({
  protocol: 'kenwood-tkx180',
  id: spec.id,
  vendor: 'Kenwood',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', 'Zone-based', '12-character names'],
  baudRate: 9600,
  stopBits: 2,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 12,
  memorySize: MEMORY_SIZE,
  identity: spec.identity,
  powerLevels: ['Low', 'High'],
}))

type Zone = {
  address: number
  count: number
  flagOffset: number
}

function zones(image: Uint8Array): Zone[] {
  const result: Zone[] = []
  for (let index = 0; index < 128; index += 1) {
    const pointer = ZONE_START + index * 2
    const address = image[pointer] | (image[pointer + 1] << 8)
    if (address === 0xffff) break
    if (image[address + 1] !== 0x31) {
      throw new Error('This Kenwood image contains a non-conventional zone.')
    }
    result.push({
      address,
      flagOffset: image[address + 2] | (image[address + 3] << 8),
      count: image[address + 4],
    })
  }
  return result
}

function channelLocations(
  image: Uint8Array,
): Map<number, { offset: number; scanIndex: number }> {
  const locations = new Map<number, { offset: number; scanIndex: number }>()
  for (const zone of zones(image)) {
    for (let index = 0; index < zone.count; index += 1) {
      const offset = zone.address + 0x20 + index * CHANNEL_SIZE
      const number = image[offset]
      if (number > 0) {
        locations.set(number, { offset, scanIndex: zone.flagOffset + index - 1 })
      }
    }
  }
  return locations
}

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value & 0x2000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x1ff).toString(8)),
      polarity: value & 0x8000 ? 'R' : 'N',
    }
  }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DCS code is not supported by this radio.')
    }
    value = Number.parseInt(channel.dtcs.toString(), 8) | 0x2800
    if (channel.dtcsPolarity === 'R') value |= 0x8000
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0 || value === 0xff ? 0x20 : value)),
  ).trim()
}

function skipFor(image: Uint8Array, scanIndex: number): boolean {
  return (image[SKIP_START + Math.floor(scanIndex / 8)] & (1 << (scanIndex & 7))) !== 0
}

function encodeChannel(
  raw: Uint8Array,
  channel: WebChannel,
  scanIndex: number,
  image: Uint8Array,
): void {
  encodeLbcd(channel.rxFrequency ?? 0, raw.subarray(1, 5))
  const tx = channel.txFrequency
  if (tx) encodeLbcd(tx, raw.subarray(5, 9))
  else raw.subarray(5, 9).fill(0xff)
  const tone = encodeTone(channel)
  raw.set(tone, 11)
  raw.set(tone, 13)
  raw.fill(0, 15, 27)
  const name = channel.name.slice(0, 12).padEnd(12, ' ')
  for (let index = 0; index < 12; index += 1) raw[15 + index] = name.charCodeAt(index) & 0xff
  raw[46] =
    (channel.power === 'High' ? 0x10 : 0) |
    (channel.mode === 'FM' ? 0x40 : 0)
  const skipOffset = SKIP_START + Math.floor(scanIndex / 8)
  const bit = 1 << (scanIndex & 7)
  image[skipOffset] = channel.skip ? image[skipOffset] | bit : image[skipOffset] & ~bit
}

export function parseTKx180Channels(image: Uint8Array): WebChannel[] {
  const result = Array.from({ length: CHANNEL_COUNT }, (_, index) => emptyChannel(index + 1))
  for (const [number, location] of channelLocations(image)) {
    if (number > CHANNEL_COUNT) continue
    const raw = image.slice(location.offset, location.offset + CHANNEL_SIZE)
    const rxFrequency = decodeLbcd(raw.slice(1, 5))
    if (!rxFrequency) continue
    const txFrequency = raw[5] === 0xff ? null : decodeLbcd(raw.slice(5, 9))
    const txTone = decodeTone(raw.slice(13, 15))
    const rxtone = decodeTone(raw.slice(11, 13))
    const selected = txTone.mode === 'none' ? rxtone : txTone
    result[number - 1] = {
      number,
      empty: false,
      name: decodeName(raw.slice(15, 27)),
      rxFrequency,
      txFrequency,
      toneMode: selected.mode,
      ctcss: selected.ctcss,
      dtcs: selected.dtcs,
      dtcsPolarity: selected.polarity,
      mode: raw[46] & 0x40 ? 'FM' : 'NFM',
      power: raw[46] & 0x10 ? 'High' : 'Low',
      skip: skipFor(image, location.scanIndex),
    }
  }
  return result
}

export function parseTKx180Channel(
  image: Uint8Array,
  number: number,
): WebChannel {
  return parseTKx180Channels(image)[number - 1] ?? emptyChannel(number)
}

export function applyTKx180Channel(
  image: Uint8Array,
  channel: WebChannel,
): void {
  const location = channelLocations(image).get(channel.number)
  if (!location) {
    throw new Error('This zone image has no writable slot for that channel number.')
  }
  if (channel.empty) {
    image[location.offset] = 0xff
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  encodeChannel(
    image.subarray(location.offset, location.offset + CHANNEL_SIZE),
    channel,
    location.scanIndex,
    image,
  )
}

async function readAck(transport: RadioTransport): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error('The Kenwood radio rejected the request.')
}

async function enter(profile: TKx180Profile, transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROGRAM'))
  if ((await transport.readExact(1, 3000))[0] !== HIGH_SPEED_ACK) {
    throw new Error('The radio refused high-speed Kenwood programming mode.')
  }
  await transport.reconfigure?.({ baudRate: 19200, stopBits: 2 })
  await readAck(transport)
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 3000)
  if (!profile.identity.every((value, index) => identity[index] === value)) {
    throw new Error(`The radio does not identify as ${profile.model}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport)
}

export async function readTKx180RadioWithTransport(
  profile: TKx180Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const lowBlocks = 0xc0
    for (let block = 0; block < lowBlocks; block += 1) {
      await transport.write(Uint8Array.of(0x52, (block >> 8) & 0xff, block & 0xff))
      const marker = (await transport.readExact(1, 4000))[0]
      if (marker === 0x57) {
        image.set(await transport.readExact(256, 5000), block * 256)
      } else if (marker === 0x5a) {
        image.fill(0xff, block * 256, (block + 1) * 256)
      } else if (marker !== 0x5a) {
        throw new Error(`Unexpected Kenwood block marker at ${block}.`)
      }
      await transport.readExact(1, 2000)
      await transport.write(Uint8Array.of(ACK))
      await readAck(transport)
      onProgress?.({ current: block + 1, total: 260, label: 'Reading Kenwood TK-x180 memory' })
    }
    for (let address = 0xc000; address < MEMORY_SIZE; address += 64) {
      await transport.write(Uint8Array.of(0x53, (address >> 8) & 0xff, address & 0xff, 64))
      if ((await transport.readExact(1, 3000))[0] !== 0x58) throw new Error('Unexpected Kenwood address block.')
      image.set(await transport.readExact(64, 4000), address)
      await transport.write(Uint8Array.of(ACK))
      await readAck(transport)
    }
    await transport.write(new TextEncoder().encode('E'))
    await readAck(transport)
    return { image, reportedModel: profile.model }
  } catch (cause) {
    await transport.write(new TextEncoder().encode('E')).catch(() => undefined)
    throw cause
  }
}

export async function writeTKx180RadioWithTransport(
  profile: TKx180Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) throw new Error('The selected image is smaller than TK-x180 memory.')
  await enter(profile, transport)
  try {
    for (let block = 0; block < 0xc0; block += 1) {
      const chunk = image.slice(block * 256, (block + 1) * 256)
      if (chunk.every((value) => value === 0xff)) {
        await transport.write(Uint8Array.of(0x5a, (block >> 8) & 0xff, block & 0xff, 0xff))
      } else {
        let sum = 0
        for (const value of chunk) sum = (sum + value) & 0xff
        const frame = new Uint8Array(4 + 256)
        frame.set([0x57, (block >> 8) & 0xff, block & 0xff, 0xff])
        frame.set(chunk, 3)
        frame[3 + 256] = sum
        await transport.write(frame)
      }
      await readAck(transport)
      onProgress?.({ current: block + 1, total: 260, label: 'Writing Kenwood TK-x180 memory' })
    }
    for (let address = 0xc000; address < MEMORY_SIZE; address += 64) {
      const blockAddress = (address - 0xc000) / 64 + 0x100
      const frame = new Uint8Array(4 + 64)
      frame.set([0x58, (blockAddress >> 8) & 0xff, blockAddress & 0xff, 64])
      frame.set(image.slice(address, address + 64), 4)
      await transport.write(frame)
      await readAck(transport)
    }
    await transport.write(new TextEncoder().encode('E'))
    await readAck(transport)
  } finally {
    await transport.write(new TextEncoder().encode('E')).catch(() => undefined)
  }
}
